import type { CountingBenchmark } from '../types';

// Camera counting patterns from WorkoutVision's counting engine, which the app reuses (step 5).
// Source: https://github.com/david-dabert/workout-vision, src/lib/counting/guide-patterns.json, fetched 2026-10-13.
// WorkoutVision is MIT licensed, Copyright (c) David Dabert; only the pattern strings below are taken from it.
//
// A pattern reads "joint/rest/first[/side][/lift]": the joint whose angle is counted (knee, hip, elbow, shoulder),
// whether that angle rests high or low between reps, whether a rep starts with the lowering (eccentric) or the
// lifting (concentric) half, and for one-sided moves how sides count (both, either, together). "lift" reads the
// joint as its far segment's lift against gravity. See WorkoutVision's scripts/make-guide-patterns.mjs.

/** The WorkoutVision entries we use, unchanged. */
export const WORKOUTVISION_PATTERNS: Record<string, string> = {
  bodyweight_squat: 'knee/high/eccentric',
  squat: 'knee/high/eccentric',
  front_squat: 'knee/high/eccentric',
  goblet_squat: 'knee/high/eccentric',
  dumbbell_sumo_squat: 'knee/high/eccentric',
  barbell_jump_squat: 'knee/high/eccentric',
  banded_squat: 'knee/high/eccentric',
  wall_ball: 'knee/high/eccentric',
  pistol_squat: 'knee/high/eccentric/either',
  single_leg_box_squat: 'knee/high/eccentric/either',
  shrimp_squat: 'knee/high/eccentric/either',
  cossack_squat: 'knee/high/eccentric/both',
  sissy_squat: 'knee/high/eccentric',
  split_squat: 'knee/high/eccentric',
  bulgarian_split_squat: 'knee/high/eccentric',
  reverse_lunge: 'knee/high/eccentric/together',
  forward_lunge: 'knee/high/eccentric/together',
  walking_lunge: 'knee/high/eccentric/both',
  lateral_lunge: 'knee/high/eccentric/both',
  dumbbell_lateral_lunge: 'knee/high/eccentric/both',
  curtsy_lunge: 'knee/high/eccentric',
  step_up: 'knee/low/concentric',
  deadlift: 'hip/low/concentric',
  sumo_deadlift: 'hip/low/concentric',
  trap_bar_deadlift: 'hip/low/concentric',
  romanian_deadlift: 'hip/high/eccentric',
  dumbbell_romanian_deadlift: 'hip/high/eccentric',
  kettlebell_romanian_deadlift: 'hip/high/eccentric',
  single_leg_romanian_deadlift: 'hip/high/eccentric/either',
  good_morning: 'hip/high/eccentric',
  glute_bridge: 'hip/low/concentric',
  single_leg_glute_bridge: 'hip/low/concentric/either',
  banded_glute_bridge: 'hip/low/concentric',
  hip_thrust: 'hip/low/concentric',
  dumbbell_hip_thrust: 'hip/low/concentric',
  frog_pump: 'hip/low/concentric',
  donkey_kick: 'hip/low/concentric/either',
  side_lying_leg_raise: 'hip/high/concentric/either',
  stability_ball_hamstring_curl: 'knee/high/concentric',
  push_up: 'elbow/high/eccentric/either',
  knee_push_up: 'elbow/high/eccentric/either',
  incline_push_up: 'elbow/high/eccentric/either',
  decline_push_up: 'elbow/high/eccentric/either',
  diamond_push_up: 'elbow/high/eccentric/either',
  wide_push_up: 'elbow/high/eccentric/either',
  wall_push_up: 'elbow/high/eccentric',
  pike_push_up: 'elbow/high/eccentric',
  archer_push_up: 'elbow/high/eccentric/both',
  handstand_push_up: 'elbow/high/eccentric',
  bench_dip: 'elbow/high/eccentric',
  dip: 'elbow/high/eccentric',
  overhead_press: 'elbow/high/eccentric',
  standing_dumbbell_press: 'elbow/high/eccentric',
  arnold_press: 'elbow/high/eccentric',
  push_press: 'elbow/high/eccentric',
  lateral_raise: 'shoulder/low/concentric',
  front_raise: 'shoulder/low/concentric/either',
  upright_row: 'shoulder/low/concentric',
  prone_y_raise: 'shoulder/low/concentric/lift',
  barbell_row: 'elbow/high/concentric',
  t_bar_row: 'elbow/high/concentric',
  pendlay_row: 'elbow/high/concentric',
  dumbbell_bent_over_row: 'elbow/high/concentric',
  one_arm_dumbbell_row: 'elbow/high/concentric/either',
  inverted_row: 'elbow/high/concentric',
  banded_row: 'elbow/high/concentric',
  banded_lat_pulldown: 'elbow/high/concentric',
  pull_up: 'elbow/high/concentric/either',
  assisted_pull_up: 'elbow/high/concentric/either',
  chin_up: 'elbow/high/concentric/either',
  neutral_grip_pull_up: 'elbow/high/concentric/either',
  bicep_curl: 'elbow/high/concentric',
  hammer_curl: 'elbow/high/concentric',
  barbell_curl: 'elbow/high/concentric',
  dumbbell_overhead_tricep_extension: 'elbow/low/concentric',
  tricep_kickback: 'elbow/low/concentric',
  bird_dog: 'hip/low/concentric/both',
  hanging_knee_raise: 'hip/high/concentric',
  hanging_leg_raise: 'hip/high/concentric',
  lying_leg_raise: 'hip/high/concentric',
  seated_knee_tuck: 'hip/high/concentric',
  decline_sit_up: 'hip/high/concentric',
  v_up: 'hip/high/concentric',
  ab_wheel: 'shoulder/low/eccentric',
};

/**
 * Our camera exercises mapped to a WorkoutVision entry: the same exercise, or (marked "like") an obvious equivalent
 * for the counted joint, for example a kettlebell swing counts like a Romanian deadlift (hip hinge from standing).
 * Camera exercises without an entry have no pattern yet and show "camera, not set up".
 */
export const COUNTER_REFS: Record<string, string> = {
  // Squats
  // 'squat' rather than 'bodyweight_squat': the key brings WorkoutVision's measured squat depth margin (LIFTS.squat);
  // on its recorded barbell squat set it counts 9 of 9, the plain pattern 6 (packages/counter/src/replay.test.ts).
  squat: 'squat', prisoner_squat: 'squat', pause_squat: 'squat', sumo_squat: 'dumbbell_sumo_squat',
  single_leg_squat: 'pistol_squat', box_pistol: 'single_leg_box_squat', shrimp_squat: 'shrimp_squat', cossack_squat: 'cossack_squat',
  sissy_squat: 'sissy_squat', split_squat: 'split_squat', bulgarian_split_squat: 'bulgarian_split_squat', squat_jump: 'barbell_jump_squat',
  goblet_squat: 'goblet_squat', goblet_squat_press: 'goblet_squat', kettlebell_front_squat: 'front_squat', double_front_squat: 'front_squat',
  kettlebell_sumo_squat: 'dumbbell_sumo_squat', overhead_squat_kb: 'squat', kettlebell_thruster: 'front_squat',
  kettlebell_cossack_squat: 'cossack_squat', back_squat: 'squat', front_squat: 'front_squat', barbell_thruster: 'front_squat',
  dumbbell_goblet_squat: 'goblet_squat', dumbbell_squat: 'squat', dumbbell_thruster: 'front_squat',
  dumbbell_bulgarian_split_squat: 'bulgarian_split_squat', band_squat: 'banded_squat', wall_ball: 'wall_ball',
  // Lunges and step-ups
  reverse_lunge: 'reverse_lunge', reverse_lunge_curl: 'reverse_lunge', forward_lunge: 'forward_lunge', walking_lunge: 'walking_lunge',
  lateral_lunge: 'lateral_lunge', curtsy_lunge: 'curtsy_lunge', step_up: 'step_up', goblet_reverse_lunge: 'reverse_lunge',
  rack_lunge: 'reverse_lunge', kettlebell_lateral_lunge: 'dumbbell_lateral_lunge', lunge_pass_through: 'forward_lunge',
  kettlebell_step_up: 'step_up', dumbbell_lunge: 'reverse_lunge', dumbbell_step_up: 'step_up',
  // Hinges and bridges (swings count like a Romanian deadlift)
  kettlebell_swing: 'romanian_deadlift', one_arm_swing: 'romanian_deadlift', hand_to_hand_swing: 'romanian_deadlift',
  american_swing: 'romanian_deadlift', kettlebell_swing_deadstop: 'romanian_deadlift',
  kettlebell_deadlift: 'deadlift', suitcase_deadlift: 'deadlift', kettlebell_rdl: 'kettlebell_romanian_deadlift',
  single_leg_rdl: 'single_leg_romanian_deadlift', single_leg_kb_deadlift: 'single_leg_romanian_deadlift',
  dumbbell_single_leg_rdl: 'single_leg_romanian_deadlift', good_morning_bodyweight: 'good_morning', kettlebell_good_morning: 'good_morning',
  band_good_morning: 'good_morning', deadlift: 'deadlift', sumo_deadlift: 'sumo_deadlift', trap_bar_deadlift: 'trap_bar_deadlift',
  romanian_deadlift: 'romanian_deadlift', dumbbell_rdl: 'dumbbell_romanian_deadlift', dumbbell_walking_lunge: 'walking_lunge',
  glute_bridge: 'glute_bridge', single_leg_glute_bridge: 'single_leg_glute_bridge', band_glute_bridge: 'banded_glute_bridge',
  hip_thrust_bodyweight: 'hip_thrust', kettlebell_hip_thrust: 'dumbbell_hip_thrust', frog_pump: 'frog_pump', donkey_kick: 'donkey_kick',
  side_lying_leg_raise: 'side_lying_leg_raise', stability_ball_hamstring_curl: 'stability_ball_hamstring_curl',
  // Push
  push_up: 'push_up', knee_push_up: 'knee_push_up', wall_push_up: 'wall_push_up', incline_push_up: 'incline_push_up',
  decline_push_up: 'decline_push_up', diamond_push_up: 'diamond_push_up', wide_push_up: 'wide_push_up', pike_push_up: 'pike_push_up',
  archer_push_up: 'archer_push_up', handstand_push_up: 'handstand_push_up', spiderman_push_up: 'push_up', clap_push_up: 'push_up',
  bench_dip: 'bench_dip', dip: 'dip',
  kettlebell_press: 'standing_dumbbell_press', double_press: 'overhead_press', bottoms_up_press: 'standing_dumbbell_press',
  half_kneeling_press: 'standing_dumbbell_press', kettlebell_push_press: 'push_press', kettlebell_jerk: 'push_press',
  overhead_press: 'overhead_press', push_press: 'push_press', dumbbell_shoulder_press: 'standing_dumbbell_press',
  arnold_press: 'arnold_press', band_overhead_press: 'standing_dumbbell_press', lateral_raise: 'lateral_raise', front_raise: 'front_raise',
  // Pull
  renegade_row: 'one_arm_dumbbell_row', kettlebell_row: 'one_arm_dumbbell_row', bent_over_double_row: 'dumbbell_bent_over_row',
  gorilla_row: 'one_arm_dumbbell_row', kettlebell_upright_row: 'upright_row', barbell_row: 'barbell_row', t_bar_row: 't_bar_row',
  pendlay_row: 'pendlay_row', dumbbell_row: 'one_arm_dumbbell_row', dumbbell_bent_over_row: 'dumbbell_bent_over_row',
  dumbbell_renegade_row: 'one_arm_dumbbell_row', inverted_row: 'inverted_row', band_row: 'banded_row', band_lat_pulldown: 'banded_lat_pulldown',
  pull_up: 'pull_up', chin_up: 'chin_up', neutral_grip_pull_up: 'neutral_grip_pull_up', band_assisted_pull_up: 'assisted_pull_up',
  prone_y_raise: 'prone_y_raise',
  // Arms
  kettlebell_curl: 'bicep_curl', barbell_curl: 'barbell_curl', dumbbell_curl: 'bicep_curl', hammer_curl: 'hammer_curl', band_curl: 'bicep_curl',
  kettlebell_triceps_extension: 'dumbbell_overhead_tricep_extension', overhead_triceps_extension: 'dumbbell_overhead_tricep_extension',
  triceps_kickback: 'tricep_kickback',
  // Core
  bird_dog: 'bird_dog', lying_leg_lowers: 'lying_leg_raise', hanging_knee_raise: 'hanging_knee_raise', hanging_leg_raise: 'hanging_leg_raise',
  toes_to_bar: 'hanging_leg_raise', sit_up: 'decline_sit_up', butterfly_sit_up: 'decline_sit_up', kettlebell_sit_up: 'decline_sit_up',
  v_up: 'v_up', tuck_up: 'seated_knee_tuck', ab_wheel_rollout: 'ab_wheel',
  // Cardio
  // A jumping jack counts like a lateral raise (the shoulder opens from the side to overhead and back). Public data,
  // hold-out final count, 14 October 2026: MM-Fit 8 of 12 exact, RepCount 3 of 11 (before: not set up, no count).
  jumping_jack: 'lateral_raise',
};

/**
 * Public-data benchmark per exercise (packages/counter/bench/run.ts, results in bench/results/2026-10-14-main.json):
 * the hold-out split, final count, with the pattern above. MM-Fit's own 3D pose (not MediaPipe), its 5 test sessions
 * held out; RepCount-pose test clips, 30 % held out by person. Additive information only: the status never changes
 * from it (decision log, 2026-10-13). Not listed: dumbbell curl (MM-Fit's curls alternate arms, ours do not).
 */
const BENCH_DATE = '2026-10-14';
const both = 'MM-Fit + RepCount-pose, hold-out', mmfit = 'MM-Fit, hold-out', repcount = 'RepCount-pose, hold-out';
const bench = (source: string, exact: number, clips: number): CountingBenchmark => ({ source, clips, exactShare: Math.round((exact / clips) * 100) / 100, date: BENCH_DATE });
export const COUNTER_BENCHMARKS: Record<string, CountingBenchmark> = {
  squat: bench(both, 19, 21),
  reverse_lunge: bench(mmfit, 15, 15),
  lateral_raise: bench(mmfit, 12, 12),
  dumbbell_shoulder_press: bench(mmfit, 11, 12),
  dumbbell_row: bench(mmfit, 11, 15),
  jumping_jack: bench(both, 11, 23),
  push_up: bench(both, 6, 20),
  sit_up: bench(both, 5, 21),
  overhead_triceps_extension: bench(mmfit, 4, 15),
  front_raise: bench(repcount, 1, 6),
  pull_up: bench(repcount, 1, 6),
};
